import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Typography from "@mui/material/Typography";
import AppCard from "@/components/AppCard.tsx";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { assertAuthenticated } from "@/api/utils.ts";
import { useTranslation } from "react-i18next";
import { useBiologyData } from "@/hooks/useBiologyData";
import { useResetBiologyDataMutation } from "@/api/biologyApi";
import { usePermissions } from "@/auth/usePermissions";
import { toast } from "react-toastify";
import { AnalysisCard, PatientHeader, FormattedUnit } from "@/components/biology";
import { formatAnalysisValue } from "@/utils/biologyFormatters";
import { ValueNormalityStatus } from "@/constants/biology";

export const Route = createFileRoute("/_dashboard/home")({
  component: Index,
  loader: ({ context }) => {
    const { session } = context;
    assertAuthenticated(session);
    return {
      user: session.user,
    };
  },
});

function Index() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patient, examinations, isLoading, isError } = useBiologyData();
  const [resetBiologyData, { isLoading: isResetting }] = useResetBiologyDataMutation();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const { isAdmin } = usePermissions();

  const handleResetConfirm = async () => {
    try {
      const result = await resetBiologyData().unwrap();
      setResetDialogOpen(false);
      if (result.success) {
        toast.success(t("home.resetSuccess", { count: result.syncCount }));
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      setResetDialogOpen(false);
      toast.error(t("home.resetError"));
      console.error("Reset error:", error);
    }
  };

  // Dernier examen
  const lastExam = examinations[0];

  // Récupérer les résultats anormaux du dernier examen
  const abnormalResults = useMemo(() => {
    return lastExam?.results.filter(
      (r) => r.normalityStatus !== ValueNormalityStatus.NORMAL
    ) ?? [];
  }, [lastExam]);

  // Récupérer les paramètres les plus fréquents sur les 5 derniers documents
  const keyResults = useMemo(() => {
    // Prendre les 5 derniers examens
    const recentExams = examinations.slice(0, 5);
    if (recentExams.length === 0) return [];

    // Compter la fréquence de chaque code et garder la dernière valeur
    const frequencyMap = new Map<string, { count: number; lastResult: typeof recentExams[0]["results"][0] }>();

    for (const exam of recentExams) {
      for (const result of exam.results) {
        const existing = frequencyMap.get(result.code);
        if (!existing) {
          frequencyMap.set(result.code, { count: 1, lastResult: result });
        } else {
          existing.count += 1;
        }
      }
    }

    // Filtrer ceux qui apparaissent plus d'une fois, trier par fréquence, prendre les 3 premiers
    return [...frequencyMap.values()]
      .filter((item) => item.count > 1)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map((item) => item.lastResult);
  }, [examinations]);

  // État de chargement
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // État d'erreur
  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t("biology.loadError")}
      </Alert>
    );
  }

  // Pas de patient
  if (!patient) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {t("biology.noPatientData")}
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* En-tête patient */}
      <Grid size={{ xs: 12 }}>
        <PatientHeader />
      </Grid>

      {/* Résultats nécessitant attention */}
      <Grid size={{ xs: 12 }}>
        <AppCard>
          <CardHeader
            title={t("home.watchValues")}
            action={
              <Button
                size="small"
                onClick={() => navigate({ to: "/results" })}
              >
                {t("home.viewAllResults")}
              </Button>
            }
          />
          <CardContent>
            {abnormalResults.length > 0 ? (
              <Grid container spacing={2}>
                {abnormalResults.slice(0, 4).map((result) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={result.id}>
                    <AnalysisCard
                      analysis={result}
                      onChartClick={(code) =>
                        navigate({ to: "/charts", search: { code } })
                      }
                      showDate={false}
                      compact
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary">
                {t("home.allNormal")}
              </Typography>
            )}
          </CardContent>
        </AppCard>
      </Grid>

      {/* Paramètres clés */}
      <Grid size={{ xs: 12, md: 8 }}>
        <AppCard>
          <CardHeader
            title={t("home.recentParams")}
            action={
              <Button
                size="small"
                onClick={() => navigate({ to: "/charts" })}
              >
                {t("home.viewCharts")}
              </Button>
            }
          />
          <CardContent>
            {keyResults.length > 0 ? (
              <Grid container spacing={2}>
                {keyResults.map((result) => {
                  const isNormal = result.normalityStatus === ValueNormalityStatus.NORMAL;

                  return (
                    <Grid size={{ xs: 12, sm: 4 }} key={result.id}>
                      <Box
                        sx={{
                          p: 2,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                        onClick={() =>
                          navigate({ to: "/charts", search: { code: result.code } })
                        }
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {result.label}
                          </Typography>
                          <Chip
                            label={isNormal ? t("biology.status.normal") : t("home.warning")}
                            size="small"
                            color={isNormal ? "success" : "warning"}
                          />
                        </Box>
                        <Typography
                          variant="h6"
                          color={isNormal ? "text.primary" : "error.main"}
                        >
                          {formatAnalysisValue(result.value, result.valueString)} <FormattedUnit unit={result.unit} />
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography color="text.secondary">
                {t("home.noParams")}
              </Typography>
            )}
          </CardContent>
        </AppCard>
      </Grid>

      {/* Actions rapides */}
      <Grid size={{ xs: 12, md: 4 }}>
        <AppCard>
          <CardHeader title={t("home.quickActions")} />
          <CardContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate({ to: "/chat" })}
              >
                {t("home.askQuestion")}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate({ to: "/upload" })}
              >
                {t("upload.title")}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate({ to: "/results" })}
              >
                {t("home.viewHistory")}
              </Button>
              {isAdmin && (
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={() => setResetDialogOpen(true)}
                  disabled={isResetting}
                >
                  {isResetting ? <CircularProgress size={20} /> : t("home.resetAnalyses")}
                </Button>
              )}
            </Box>
          </CardContent>
        </AppCard>
      </Grid>

      {/* Dialog de confirmation de reset */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>{t("home.resetTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("home.resetConfirm")}
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            {t("home.resetWarning")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={handleResetConfirm}
            color="error"
            variant="contained"
            disabled={isResetting}
          >
            {isResetting ? <CircularProgress size={20} /> : t("home.reset")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
